import type {
  AgentSubmissionStore,
  ConversationStreamStore,
  PersistenceAdapter,
  SessionStore,
} from "@fabric-harness/sdk";
import { lakebaseClient } from "./lakebase.js";
import type { LakebaseClient, LakebaseClientOptions } from "./lakebase.js";

/**
 * The slice of `@fabric-harness/node` this adapter assembles. Loaded
 * dynamically (node is an optional peer — Databricks Apps always run on
 * Node, but this package stays importable without it); injectable for tests.
 */
export interface DatabricksPersistenceStoreModule {
  PostgresSessionStore: new (options: {
    client: LakebaseClient;
    initialize?: boolean;
  }) => SessionStore;
  PostgresSubmissionStore: new (options: {
    client: LakebaseClient;
    initialize?: boolean;
  }) => AgentSubmissionStore;
  PostgresConversationStreamStore: new (options: {
    client: LakebaseClient;
    initialize?: boolean;
  }) => ConversationStreamStore;
  ensurePostgresSubmissionTables(client: LakebaseClient): Promise<void>;
  ensurePostgresConversationStreamTables(client: LakebaseClient): Promise<void>;
  postgresSchedulerLeaseStore?: (client: LakebaseClient) => DatabricksSchedulerLeaseStore;
}

interface DatabricksSchedulerLeaseStore {
  prepare?(): Promise<void>;
  claim(key: string, ownerId: string, expiresAt: number, now: number): Promise<boolean>;
  renew(key: string, ownerId: string, expiresAt: number): Promise<boolean>;
  release(key: string, ownerId: string): Promise<void>;
  readLastScheduledAt?(key: string): Promise<number | undefined>;
  recordLastScheduledAt?(key: string, scheduledAt: number): Promise<void>;
}

export interface DatabricksPersistenceOptions {
  /**
   * Lakebase connection options (workspace OAuth credential exchange or an
   * explicit local/test password), or an already-constructed client to reuse.
   */
  lakebase: LakebaseClientOptions | LakebaseClient;
  /** Override the `@fabric-harness/node` loader (tests / bundlers). */
  loadStores?: () => Promise<DatabricksPersistenceStoreModule>;
}

/**
 * Everything the v2 runtime persists, on Lakebase, in one adapter:
 * sessions (canonical conversation state), durable agent submissions
 * (admission/FIFO/leases/settlement), and offset-addressable conversation
 * streams. Implements the SDK {@link PersistenceAdapter} contract, so it
 * plugs straight into `init({ persistence })`; the extra `connect*` methods
 * feed `startDevServer({ submissionStore, conversationStreamStore })`.
 */
export interface DatabricksPersistence extends PersistenceAdapter {
  readonly client: LakebaseClient;
  connect(): Promise<SessionStore>;
  connectSubmissionStore(): Promise<AgentSubmissionStore>;
  connectConversationStreamStore(): Promise<ConversationStreamStore>;
  connectSchedulerLeaseStore(): Promise<DatabricksSchedulerLeaseStore | undefined>;
  /** All three stores in one call (server wiring convenience). */
  connectAll(): Promise<{
    store: SessionStore;
    submissionStore: AgentSubmissionStore;
    conversationStreamStore: ConversationStreamStore;
  }>;
  /** Idempotent schema setup for every store (CREATE TABLE IF NOT EXISTS). */
  migrate(): Promise<void>;
  close(): Promise<void>;
}

/**
 * One-call Lakebase persistence for Databricks-hosted agents.
 *
 * ```ts
 * const persistence = databricksPersistence({
 *   lakebase: { host, database: 'fabric', user: clientId, endpoint, credentialClient: sdk.postgres },
 * });
 * const { store, submissionStore, conversationStreamStore } = await persistence.connectAll();
 * await startDevServer({ submissionStore, conversationStreamStore });
 * ```
 *
 * The Postgres store implementations come from `@fabric-harness/node`
 * (optional peer) and run unchanged against Lakebase — the only Databricks
 * specifics are database-credential exchange, refresh caching, and TLS defaults from
 * {@link lakebaseClient}.
 */
export function databricksPersistence(
  options: DatabricksPersistenceOptions,
): DatabricksPersistence {
  const client = isLakebaseClient(options.lakebase)
    ? options.lakebase
    : lakebaseClient(options.lakebase);
  const ownsClient = !isLakebaseClient(options.lakebase);
  const loadStores = options.loadStores ?? defaultStoreLoader;

  let modulePromise: Promise<DatabricksPersistenceStoreModule> | undefined;
  const stores = () => {
    modulePromise ??= loadStores();
    return modulePromise;
  };

  let sessionStore: SessionStore | undefined;
  let submissionStore: AgentSubmissionStore | undefined;
  let conversationStreamStore: ConversationStreamStore | undefined;
  let schedulerLeaseStore: DatabricksSchedulerLeaseStore | undefined;

  const persistence: DatabricksPersistence = {
    client,

    async connect() {
      const module = await stores();
      sessionStore ??= new module.PostgresSessionStore({ client });
      return sessionStore;
    },

    async connectSubmissionStore() {
      const module = await stores();
      submissionStore ??= new module.PostgresSubmissionStore({ client });
      return submissionStore;
    },

    async connectConversationStreamStore() {
      const module = await stores();
      conversationStreamStore ??= new module.PostgresConversationStreamStore({ client });
      return conversationStreamStore;
    },

    async connectSchedulerLeaseStore() {
      const module = await stores();
      schedulerLeaseStore ??= module.postgresSchedulerLeaseStore?.(client);
      return schedulerLeaseStore;
    },

    async connectAll() {
      return {
        store: await persistence.connect(),
        submissionStore: await persistence.connectSubmissionStore(),
        conversationStreamStore: await persistence.connectConversationStreamStore(),
      };
    },

    async migrate() {
      const module = await stores();
      // Session tables initialize inside the store constructor; the
      // submission/stream ensure functions are exported directly.
      await persistence.connect();
      await module.ensurePostgresSubmissionTables(client);
      await module.ensurePostgresConversationStreamTables(client);
    },

    async close() {
      if (ownsClient) await client.end();
    },
  };

  return persistence;
}

function isLakebaseClient(
  value: DatabricksPersistenceOptions["lakebase"],
): value is LakebaseClient {
  return typeof (value as LakebaseClient).query === "function";
}

async function defaultStoreLoader(): Promise<DatabricksPersistenceStoreModule> {
  try {
    // Dynamic specifier keeps bundlers from hard-wiring the optional peer.
    const specifier = "@fabric-harness/node";
    return (await import(specifier)) as unknown as DatabricksPersistenceStoreModule;
  } catch (error) {
    throw new Error(
      `databricksPersistence() requires the optional "@fabric-harness/node" package (it provides the Postgres store implementations). Install it alongside @fabric-harness/databricks. ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}
